import { Star } from 'lucide-react';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import { Card, CardBody, Progress } from '@vnetwork/ui/primitives';
import type { RatingSummary } from '@/api/internal-ratings';

interface RatingSummaryCardProps {
  summary: RatingSummary;
  /** Currently active star filter (1–5). Undefined = no filter. */
  activeStar?: number;
  /** Called with the star value when a bar row is clicked. */
  onStarFilter?: (star: number) => void;
}

/**
 * Displays the signed-in employee's overall rating summary: average score,
 * total count, and a 1–5 star distribution bar chart. Each bar row is
 * clickable when `onStarFilter` is provided — clicking filters the received
 * ratings table to that star bucket (clicking the active star clears it).
 */
export function RatingSummaryCard({ summary, activeStar, onStarFilter }: RatingSummaryCardProps) {
  return (
    <Card flat>
      <CardBody>
        <StatGroup columns={2}>
          <Stat
            label="Average rating"
            dotColor="var(--vn-warning-solid)"
            value={
              summary.average_rating != null
                ? `${summary.average_rating.toFixed(2)} / 5`
                : '—'
            }
          />
          <Stat
            label="Total ratings"
            dotColor="var(--vn-info-solid)"
            value={summary.total_ratings ?? '—'}
          />
        </StatGroup>

        {summary.distribution && summary.distribution.length > 0 && (
          <div style={{ marginTop: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
            {[5, 4, 3, 2, 1].map((star) => {
              const bucket = summary.distribution.find((b) => b.star === star);
              const count = bucket?.count ?? 0;
              const pct = summary.total_ratings > 0
                ? Math.round((count / summary.total_ratings) * 100)
                : 0;
              const isActive = activeStar === star;
              const isClickable = !!onStarFilter;

              return (
                <div
                  key={star}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onClick={isClickable ? () => onStarFilter(star) : undefined}
                  onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStarFilter(star); } } : undefined}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '60px 1fr 36px',
                    alignItems: 'center',
                    gap: 'var(--vn-space-2)',
                    fontSize: 'var(--vn-font-size-sm)',
                    color: isActive ? 'var(--vn-warning-text)' : 'var(--vn-fg-muted)',
                    cursor: isClickable ? 'pointer' : undefined,
                    padding: 'var(--vn-space-1) var(--vn-space-2)',
                    borderRadius: 'var(--vn-radius-sm)',
                    background: isActive ? 'var(--vn-warning-subtle)' : 'transparent',
                    outline: 'none',
                    transition: 'background 0.15s',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', fontWeight: isActive ? 'var(--vn-font-weight-semibold)' : undefined }}>
                    <Star size={13} style={{ color: 'var(--vn-warning-solid)', flexShrink: 0 }} />
                    {star} star
                  </span>
                  <Progress value={pct} max={100} tone="warning" size="sm" />
                  <span style={{ textAlign: 'right' }}>{count}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
